import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ContentCopy } from "@mui/icons-material";
import { Alert, Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import {
  hydrateLayer,
  selectDraftConfigEntities,
  transformStylePayload,
  useGetVersionConfigQuery,
  useUpdateLayerConfigMutation,
  useUpdateSublayerConfigMutation,
} from "fond/api";
import { useEditorContext } from "fond/styleEditor/Editor/EditorProvider";
import { Style } from "fond/types";
import { LayerConfig, LayerConfigHydrated, SublayerConfig, SublayerConfigHydrated } from "fond/types/ProjectLayerConfig";
import { Modal } from "fond/widgets";

import CopyStylesForm, { ICopyStylesFormData } from "./CopyStylesForm";
import { extractAttributesFromFilter, transformSublayerForCopying } from "./helper";

import { ContentWrapper } from "./CopyStyles.styles";

interface IProps {
  layerConfig: LayerConfig | SublayerConfig;
  mapLayerConfigId: string;
  onClose(): void;
}

const CopyStyles: React.FC<IProps> = ({ layerConfig, mapLayerConfigId, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const draft = useSelector(selectDraftConfigEntities);
  const [updateLayerConfig] = useUpdateLayerConfigMutation();
  const [updateSubLayerConfig] = useUpdateSublayerConfigMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { copyStylesSourceProject, copyStylesSourceVersion } = useEditorContext();
  const [formValues, setFormValues] = useState<ICopyStylesFormData>({
    selectedLayerStyles: [],
    selectedSublayers: [],
    OverwriteStyles: false,
    Project: copyStylesSourceProject,
    Version: copyStylesSourceVersion,
  });

  // Fetch the source version config to get full sublayer data
  const { data: sourceConfig } = useGetVersionConfigQuery(formValues.Version?.ID ?? "", {
    skip: !formValues.Version?.ID,
  });

  // Get destination layer attributes
  const destinationLayer = useMemo(() => {
    if (layerConfig.Type === "SUBLAYER") {
      // For sublayers, get the parent layer's attributes
      const parentLayer = draft[layerConfig.ParentID];
      return parentLayer?.Type === "LAYER" ? parentLayer : null;
    }
    return layerConfig;
  }, [draft, layerConfig]);

  // Validate that selected sublayers reference attributes that exist in destination layer
  const attributeValidation = useMemo(() => {
    if (!sourceConfig || formValues.selectedSublayers?.length === 0) {
      return { isValid: true, missingAttributes: [] };
    }

    const destinationAttributes = new Set((destinationLayer?.Attributes || []).map((attr) => attr.Name));

    const missingAttributes: Array<{ sublayerLabel: string; missingAttrs: string[] }> = [];

    formValues.selectedSublayers?.forEach((sublayer) => {
      const referencedAttrs = extractAttributesFromFilter(sublayer.FilterConfiguration);
      const missing = referencedAttrs.filter((attr) => !destinationAttributes.has(attr));

      if (missing.length > 0) {
        missingAttributes.push({
          sublayerLabel: sublayer.Label,
          missingAttrs: missing,
        });
      }
    });

    return {
      isValid: missingAttributes.length === 0,
      missingAttributes,
    };
  }, [sourceConfig, formValues.selectedSublayers, destinationLayer]);

  // Validate that we're not adding duplicate "other" type sublayers
  const otherTypeValidation = useMemo(() => {
    // Only validate if not overwriting and we have selected sublayers
    if (formValues.OverwriteStyles || formValues.selectedSublayers?.length === 0 || layerConfig.Type !== "LAYER") {
      return { isValid: true, conflictingSublayers: [] };
    }

    // Check if destination layer already has an "other" type sublayer
    const destinationLayerConfig = destinationLayer as LayerConfig;
    const hasOtherTypeSublayer = destinationLayerConfig?.Children?.some((childId) => {
      const child = draft[childId];
      return child?.Type === "SUBLAYER" && child.FilterConfiguration?.Type === "other";
    });

    if (!hasOtherTypeSublayer) {
      return { isValid: true, conflictingSublayers: [] };
    }

    // Find selected sublayers with Type = "other"
    const conflictingSublayers =
      formValues.selectedSublayers?.filter((sublayer) => sublayer.FilterConfiguration?.Type === "other").map((sublayer) => sublayer.Label) || [];

    return {
      isValid: conflictingSublayers.length === 0,
      conflictingSublayers,
    };
  }, [formValues.selectedSublayers, formValues.OverwriteStyles, destinationLayer, draft, layerConfig.Type]);

  const handleOnCancel = () => {
    onClose();
  };

  /**
   * Applies the selected styles and/or sublayers to the layer/sublayer.
   */
  const handleOnSubmit = async () => {
    const selectedStyles = formValues.selectedLayerStyles || [];
    const selectedSublayers = formValues.selectedSublayers || [];

    // Validate: Cannot copy sublayers to a sublayer
    if (selectedSublayers.length > 0 && layerConfig.Type === "SUBLAYER") {
      enqueueSnackbar("Cannot copy sublayers to a sublayer. Sublayers can only be added to layers.", { variant: "error" });
      return;
    }

    // Validate: Must have source config to copy sublayers
    if (selectedSublayers.length > 0 && !sourceConfig) {
      enqueueSnackbar("Source configuration not loaded", { variant: "error" });
      return;
    }

    const newLayer = hydrateLayer(draft, layerConfig);

    // If overwriting, clear both styles and sublayers first
    if (formValues.OverwriteStyles) {
      newLayer.Styles = [];
      if (layerConfig.Type === "LAYER") {
        (newLayer as LayerConfigHydrated).Children = [];
      }
    }

    // Handle styles
    if (selectedStyles.length > 0) {
      const selectedStylesPayload = selectedStyles.map((style, styleIndex) => ({
        ...transformStylePayload(style, styleIndex, true),
        ConfigurationType: layerConfig.Type,
        ConfigurationID: layerConfig.ID,
      })) as Style[];

      // Append to cleared (if overwriting) or existing styles
      newLayer.Styles = [...newLayer.Styles, ...selectedStylesPayload];

      // Correct the style positions
      newLayer.Styles = newLayer.Styles.map((style, index) => ({ ...style, Position: index }));
    }

    // Handle sublayers (only for layers, not sublayers)
    if (selectedSublayers.length > 0 && layerConfig.Type === "LAYER" && newLayer.Type === "LAYER" && sourceConfig) {
      const newSublayers = selectedSublayers.map((sublayer) => transformSublayerForCopying(sublayer, sourceConfig, layerConfig.ID));

      // Append to cleared (if overwriting) or existing sublayers
      newLayer.Children = [...(newLayer as LayerConfigHydrated).Children, ...newSublayers];

      // Correct the sublayer positions
      newLayer.Children = (newLayer as LayerConfigHydrated).Children.map((child, index) => ({
        ...child,
        Position: index,
      }));
    }

    setIsSubmitting(true);
    try {
      if (layerConfig.Type === "SUBLAYER") {
        await updateSubLayerConfig({ sublayer: newLayer as SublayerConfigHydrated, mapLayerConfigId });
      } else {
        await updateLayerConfig({ layerConfig: newLayer as LayerConfigHydrated, mapLayerConfigId });
      }
      setIsSubmitting(false);
      onClose();
      enqueueSnackbar(`Successfully copied ${selectedStyles.length} style(s) and ${selectedSublayers.length} sublayer(s)`, { variant: "success" });
    } catch (error) {
      setIsSubmitting(false);
      enqueueSnackbar("Failed to update layer configuration", { variant: "error" });
    }
  };

  const hasSelectedItems = (formValues.selectedLayerStyles?.length ?? 0) > 0 || (formValues.selectedSublayers?.length ?? 0) > 0;
  const canSubmit = hasSelectedItems && attributeValidation.isValid && otherTypeValidation.isValid;

  return (
    <Modal
      open
      onClose={onClose}
      headerIcon={<ContentCopy />}
      header="Copy styles and sublayers"
      data-testid="copy-styles-modal"
      showFullscreen
      showClose
      variant="primary"
      DialogContentProps={{ style: { display: "flex", flexDirection: "column", minHeight: 0 } }}
      content={
        <ContentWrapper>
          {!attributeValidation.isValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Missing Attributes:</strong> The following sublayers reference attributes that do not exist in the destination layer:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {attributeValidation.missingAttributes.map((item) => (
                  <li key={item.sublayerLabel}>
                    <strong>{item.sublayerLabel}</strong>: {item.missingAttrs.join(", ")}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {!otherTypeValidation.isValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Duplicate "Other" Type:</strong> The destination layer already has a sublayer with filter type "other". The following sublayers
              cannot be copied without overwriting:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {otherTypeValidation.conflictingSublayers.map((label) => (
                  <li key={label}>
                    <strong>{label}</strong>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <CopyStylesForm
            initialValues={formValues}
            onChange={setFormValues}
            isSubmitting={isSubmitting}
            allowSublayerSelection={layerConfig.Type === "LAYER"}
          />
        </ContentWrapper>
      }
      actions={
        <>
          <Button data-testid="new-style-cancel-button" color="primary" onClick={handleOnCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <LoadingButton
            data-testid="copy-style-save-button"
            color="primary"
            type="submit"
            onClick={handleOnSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Apply
          </LoadingButton>
        </>
      }
    />
  );
};

export default CopyStyles;
